import { useState, useRef, useEffect } from "react";
import { categories } from "@/mocks/categories";
import { useNavigate } from "react-router-dom";

export default function Navbar() {
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [deskDropdown, setDeskDropdown] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const mobileMenuRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setDeskDropdown(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    if (mobileOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => {
      document.body.style.overflow = "";
    };
  }, [mobileOpen]);

  const handleCategorySelect = (url: string) => {
    setDeskDropdown(false);
    setMobileOpen(false);
    window.open(url, "_blank", "noopener,noreferrer");
  };

  const articleLinks = [
    { label: "Complete Guide", to: "/hipobuy-spreadsheet-complete-guide" },
    { label: "vs Kakobuy", to: "/hipobuy-spreadsheet-vs-kakobuy" },
    { label: "vs CSSBuy", to: "/hipobuy-spreadsheet-vs-cssbuy" },
    { label: "vs Superbuy", to: "/hipobuy-spreadsheet-vs-superbuy" },
    { label: "vs Oopbuy", to: "/hipobuy-spreadsheet-vs-oopbuy" },
    { label: "Category Deep-Dive", to: "/hipobuy-spreadsheet-category-deep-dive" },
  ];

  return (
    <>
      <nav
        className={`fixed top-0 left-0 w-full z-50 transition-all duration-300 ${
          scrolled
            ? "bg-background-50/95 backdrop-blur-md border-b border-background-200/70"
            : "bg-transparent"
        }`}
      >
        <div className="max-w-7xl mx-auto px-4 md:px-6 flex items-center justify-between h-16 md:h-[72px]">
          <a
            href="/"
            className="flex items-center gap-2 no-underline cursor-pointer group"
          >
            <div className="w-8 h-8 md:w-9 md:h-9 rounded-lg bg-primary-500 flex items-center justify-center">
              <i className="ri-table-line text-lg text-white"></i>
            </div>
            <span className="text-lg md:text-xl font-heading text-foreground-900 whitespace-nowrap group-hover:text-primary-600 transition-colors">
              Hipobuy Spreadsheet
            </span>
          </a>

          <div className="hidden md:flex items-center gap-4">
            <div className="relative" ref={dropdownRef}>
              <button
                onClick={() => setDeskDropdown(!deskDropdown)}
                className="flex items-center gap-2 px-4 py-2 rounded-lg bg-background-100 border border-background-200/70 text-foreground-700 text-sm font-medium hover:bg-background-200 transition-colors cursor-pointer whitespace-nowrap"
              >
                <i className="ri-grid-line text-base"></i>
                Browse Categories
                <i className={`ri-arrow-down-s-line text-base transition-transform duration-200 ${deskDropdown ? "rotate-180" : ""}`}></i>
              </button>

              {deskDropdown && (
                <div className="absolute top-full mt-2 left-0 w-72 bg-background-50 border border-background-200/70 rounded-xl shadow-lg py-2 max-h-96 overflow-y-auto z-50">
                  <div className="px-4 py-2 text-xs font-semibold text-foreground-400 uppercase tracking-wider">
                    Product Categories
                  </div>
                  {categories.map((cat) => (
                    <button
                      key={cat.name}
                      onClick={() => handleCategorySelect(cat.url)}
                      className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-foreground-700 hover:bg-primary-50 hover:text-primary-700 transition-colors cursor-pointer whitespace-nowrap text-left"
                    >
                      <div className="w-7 h-7 flex items-center justify-center rounded-md bg-secondary-100">
                        <i className={`${cat.icon} text-xs text-secondary-600`}></i>
                      </div>
                      {cat.name}
                    </button>
                  ))}
                  <div className="border-t border-background-200/60 mt-2 pt-2">
                    <div className="px-4 py-2 text-xs font-semibold text-foreground-400 uppercase tracking-wider">
                      Research Guides
                    </div>
                    {articleLinks.map((link) => (
                      <button
                        key={link.to}
                        onClick={() => {
                          setDeskDropdown(false);
                          navigate(link.to);
                        }}
                        className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-foreground-700 hover:bg-accent-50 hover:text-accent-700 transition-colors cursor-pointer whitespace-nowrap text-left"
                      >
                        <div className="w-7 h-7 flex items-center justify-center rounded-md bg-accent-100">
                          <i className="ri-article-line text-xs text-accent-600"></i>
                        </div>
                        {link.label}
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>

            <div className="relative">
              <button
                onClick={() => setDropdownOpen(!dropdownOpen)}
                className="flex items-center gap-2 px-4 py-2 rounded-lg bg-background-100 border border-background-200/70 text-foreground-700 text-sm font-medium hover:bg-background-200 transition-colors cursor-pointer whitespace-nowrap"
              >
                <i className="ri-article-line text-base"></i>
                Research Guides
                <i className={`ri-arrow-down-s-line text-base transition-transform duration-200 ${dropdownOpen ? "rotate-180" : ""}`}></i>
              </button>

              {dropdownOpen && (
                <>
                  <div className="fixed inset-0 z-40" onClick={() => setDropdownOpen(false)}></div>
                  <div className="absolute top-full mt-2 right-0 w-56 bg-background-50 border border-background-200/70 rounded-xl shadow-lg py-2 z-50">
                    {articleLinks.map((link) => (
                      <button
                        key={link.to}
                        onClick={() => {
                          setDropdownOpen(false);
                          navigate(link.to);
                        }}
                        className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-foreground-700 hover:bg-accent-50 hover:text-accent-700 transition-colors cursor-pointer whitespace-nowrap text-left"
                      >
                        <div className="w-6 h-6 flex items-center justify-center">
                          <i className="ri-arrow-right-s-line text-sm"></i>
                        </div>
                        {link.label}
                      </button>
                    ))}
                  </div>
                </>
              )}
            </div>

            <a
              href="https://kaspreadsheet.com"
              target="_blank"
              rel="noopener nofollow"
              className="px-4 py-2 rounded-lg border-2 border-primary-500 text-primary-600 text-sm font-semibold hover:bg-primary-50 transition-colors cursor-pointer whitespace-nowrap"
            >
              Hipobuy Sheet
            </a>

            <a
              href="https://kaspreadsheet.com"
              target="_blank"
              rel="noopener nofollow"
              className="px-5 py-2.5 rounded-lg bg-primary-500 text-white text-sm font-semibold hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
            >
              Buy It Now
            </a>
          </div>

          <div className="flex md:hidden items-center gap-2">
            <a
              href="https://kaspreadsheet.com"
              target="_blank"
              rel="noopener nofollow"
              className="px-3 py-1.5 rounded-lg bg-primary-500 text-white text-xs font-semibold hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
            >
              Buy Now
            </a>
            <button
              onClick={() => setMobileOpen(!mobileOpen)}
              className="w-9 h-9 flex items-center justify-center rounded-lg bg-background-100 border border-background-200/70 cursor-pointer relative"
              aria-label="Toggle menu"
            >
              <i className={`text-lg transition-all duration-200 ${mobileOpen ? "ri-close-line" : "ri-menu-line"}`}></i>
            </button>
          </div>
        </div>
      </nav>

      {mobileOpen && (
        <>
          <div
            className="fixed inset-0 bg-black/40 z-40 md:hidden backdrop-blur-sm transition-opacity"
            onClick={() => setMobileOpen(false)}
          ></div>

          <div
            ref={mobileMenuRef}
            className="fixed top-16 left-0 right-0 bottom-0 z-50 bg-background-50 overflow-y-auto md:hidden animate-slide-in"
          >
            <div className="px-4 py-5 space-y-6">
              <div>
                <h4 className="text-xs font-semibold text-foreground-400 uppercase tracking-wider mb-3 px-1">
                  Quick Actions
                </h4>
                <div className="space-y-2">
                  <a
                    href="https://kaspreadsheet.com"
                    target="_blank"
                    rel="noopener nofollow"
                    onClick={() => setMobileOpen(false)}
                    className="flex items-center justify-between w-full px-4 py-3 rounded-xl bg-primary-500 text-white text-sm font-semibold hover:bg-primary-600 transition-colors cursor-pointer"
                  >
                    <span>Buy It Now</span>
                    <div className="w-5 h-5 flex items-center justify-center">
                      <i className="ri-external-link-line text-sm"></i>
                    </div>
                  </a>
                  <a
                    href="https://kaspreadsheet.com"
                    target="_blank"
                    rel="noopener nofollow"
                    onClick={() => setMobileOpen(false)}
                    className="flex items-center justify-between w-full px-4 py-3 rounded-xl border-2 border-primary-500 text-primary-600 text-sm font-semibold hover:bg-primary-50 transition-colors cursor-pointer"
                  >
                    <span>Hipobuy Sheet</span>
                    <div className="w-5 h-5 flex items-center justify-center">
                      <i className="ri-external-link-line text-sm"></i>
                    </div>
                  </a>
                </div>
              </div>

              <div>
                <h4 className="text-xs font-semibold text-foreground-400 uppercase tracking-wider mb-3 px-1">
                  Product Categories
                </h4>
                <div className="grid grid-cols-2 gap-2">
                  {categories.map((cat) => (
                    <button
                      key={cat.name}
                      onClick={() => handleCategorySelect(cat.url)}
                      className="flex items-center gap-2.5 px-3 py-3 rounded-xl bg-background-100 border border-background-200/60 text-sm text-foreground-700 hover:bg-primary-50 hover:border-primary-300 hover:text-primary-700 transition-all duration-200 cursor-pointer active:scale-[0.98]"
                    >
                      <div className="w-8 h-8 rounded-lg bg-secondary-100 flex items-center justify-center flex-shrink-0">
                        <i className={`${cat.icon} text-sm text-secondary-600`}></i>
                      </div>
                      <span className="text-xs font-medium text-left leading-tight">{cat.name}</span>
                    </button>
                  ))}
                </div>
              </div>

              <div>
                <h4 className="text-xs font-semibold text-foreground-400 uppercase tracking-wider mb-3 px-1">
                  Research Guides &amp; Comparisons
                </h4>
                <div className="space-y-1">
                  {articleLinks.map((link) => (
                    <button
                      key={link.to}
                      onClick={() => {
                        setMobileOpen(false);
                        navigate(link.to);
                      }}
                      className="flex items-center justify-between w-full px-4 py-3 rounded-xl text-sm text-foreground-700 hover:bg-accent-50 hover:text-accent-700 transition-colors cursor-pointer"
                    >
                      <div className="flex items-center gap-3">
                        <div className="w-7 h-7 rounded-lg bg-accent-100 flex items-center justify-center">
                          <i className="ri-article-line text-xs text-accent-600"></i>
                        </div>
                        <span className="text-sm font-medium">{link.label}</span>
                      </div>
                      <div className="w-5 h-5 flex items-center justify-center">
                        <i className="ri-arrow-right-s-line text-foreground-400 text-sm"></i>
                      </div>
                    </button>
                  ))}
                </div>
              </div>

              <div className="pt-3 pb-6 border-t border-background-200/60">
                <a
                  href="/"
                  onClick={() => setMobileOpen(false)}
                  className="flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm text-foreground-500 hover:text-foreground-800 transition-colors cursor-pointer"
                >
                  <div className="w-5 h-5 flex items-center justify-center">
                    <i className="ri-home-4-line text-sm"></i>
                  </div>
                  Back to Homepage
                </a>
              </div>
            </div>
          </div>
        </>
      )}
    </>
  );
}